/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Cliente } from 'src/app/models/Cliente';
import { Pedido } from 'src/app/models/Pedido';
import { PostPedido } from 'src/app/models/PostPedido';
import { Producto } from 'src/app/models/Producto';
import { Sucursal } from 'src/app/models/Sucursal';
import { Unidad } from 'src/app/models/Unidad';
import { User } from 'src/app/models/User.model';
import { Precio } from 'src/app/models/Precio';
import { PostItem } from 'src/app/models/PostItem';
import { PedidoApiService } from './pedido-api.service';
import { PedidoTotalsService } from './pedido-totals.service';

export interface FormProductos {
  id?: number;
  unidad: string;
  unidad_id?: number;
  unidadModel: Unidad;
  cantidad: number;
  precio_lista: string;
  lista_id?: string;
  lista?: any;
  precioModel: Precio;
  precio_unidad: number;
  iva: string;
  dto1: number;
  dto2: number;
  dto1_value: number;
  dto2_value: number;
  almacen: string;
  almacen_id?: number;
  bruto: number;
  iva_value: number;
  subtotal: number;
  impuesto: number;
  priceBy: string;
}
export interface FormCliente {
  forma_entrega?: string;
  cond_venta?: string;
  to_mail?: string;
  sucursalNombre?: string;
  sucursal_id?: number;
  repartidor?: string;
  piso?: number | string;
  dpto?: number | string;
  fromHorario?: string;
  toHorario?: string;
  horario?: string;
  domicilio?: any;
  telefono: string;
  email?: string;
  localidad?: string;
  codigoPostal?: string;
  provincia_id?: number;
}

export interface PedidoForm {
  id?: number;
  cliente?: Cliente;
  productos_cargados?: Producto[];
  detalles_prod_cargados?: FormProductos[];
  detalles_cliente: FormCliente;
  bruto?: number;
  ivaValor?: number;
  descuentos?: number;
  total?: number;
  observaciones?: string;
}

export type AddedMontos = {
  iva: number;
  sub_total: number;
  bruto: number;
  descuento: number;
};

@Injectable({
  providedIn: 'root',
})
export class PedidoService {
  private pedido: BehaviorSubject<PedidoForm> = new BehaviorSubject(
    {} as PedidoForm
  );
  private pedidoForEdit: BehaviorSubject<Pedido> = new BehaviorSubject(
    {} as Pedido
  );
  private productosEditados: BehaviorSubject<Array<Producto>> =
    new BehaviorSubject([]);
  private detallesProductosEditados: BehaviorSubject<Array<FormProductos>> =
    new BehaviorSubject([]);
  private _montosAdded: BehaviorSubject<AddedMontos> = new BehaviorSubject({
    iva: 0,
    sub_total: 0,
    bruto: 0,
    descuento: 0,
  });
  _cleanse: Subject<void> = new Subject();

  constructor(
    private api: PedidoApiService,
    private totals: PedidoTotalsService
  ) {
    // Rehidratar borrador al iniciar
    const draft = this.loadDraft();
    if (draft) {
      this.pedido.next(draft);
      // NO restaurar los BehaviorSubjects - estos son solo para productos en edición temporal
      // Los productos del draft ya están en state.productos_cargados y state.detalles_prod_cargados
    } else {
    }
  }

  // ── Observables ──────────────────────────────────────────
  get state(): Observable<PedidoForm> {
    return this.pedido.asObservable();
  }
  get editEtate(): Observable<Pedido> {
    return this.pedidoForEdit.asObservable();
  }
  get productosAdded(): Observable<Array<Producto>> {
    return this.productosEditados.asObservable();
  }
  get detallesProductosAdded(): Observable<Array<FormProductos>> {
    return this.detallesProductosEditados.asObservable();
  }
  get value(): PedidoForm {
    return this.pedido.value;
  }
  get valueEdit(): Pedido {
    return this.pedidoForEdit.value;
  }
  get montosAdded(): AddedMontos {
    return this._montosAdded.value;
  }
  get montosAddedObs(): Observable<AddedMontos> {
    return this._montosAdded.asObservable();
  }
  get cleanse(): Observable<void> {
    return this._cleanse.asObservable();
  }
  get hasDraft(): boolean {
    return !!this.value?.cliente;
  }
  // ── Draft navigation helper ────────────────────────────────
  getDraftRoute(): string[] {
    const state = this.value;
    if (!state?.cliente) return ['/pedidos/crear/clientes'];
    if (state.productos_cargados?.length || state.detalles_prod_cargados?.length) {
      return ['/pedidos/crear/listado-productos'];
    }
    return ['/pedidos/crear/agregar-productos'];
  }

  // ── Storage helpers ───────────────────────────────────────
  private readonly DRAFT_KEY = 'pedido_draft_v1';

  private loadDraft(): PedidoForm | null {
    try {
      const raw = localStorage.getItem(this.DRAFT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
        // Basic shape validation
      if (parsed && typeof parsed === 'object') {
        // Check if it's the new format with pedidoForm (even if pedidoForEditId is undefined)
        if (parsed.pedidoForm) {
          // Restore pedidoForEdit with the ID if it exists
          if (parsed.pedidoForEditId) {
            this.pedidoForEdit.next({ id: parsed.pedidoForEditId } as Pedido);
          }
          const pedidoForm = parsed.pedidoForm as PedidoForm;
          return pedidoForm;
        }
        // Legacy format (just PedidoForm)
        return parsed as PedidoForm;
      }
    } catch (e) {
      console.warn('[PedidoService] Failed to load draft, clearing corrupted data', e);
    }
    localStorage.removeItem(this.DRAFT_KEY);
    return null;
  }

  private saveDraft(state: PedidoForm) {
    try {
      const draftData = {
        pedidoForm: state,
        pedidoForEditId: this.pedidoForEdit.value.id
      };
      localStorage.setItem(this.DRAFT_KEY, JSON.stringify(draftData));
    } catch (e) {
      console.warn('[PedidoService] Failed to save draft', e);
    }
  }

  private clearDraft() {
    localStorage.removeItem(this.DRAFT_KEY);
  }

  // ── State mutations ──────────────────────────────────────
  setCliente(cliente: Cliente) {
    const newState = { ...this.value, cliente };
    this.pedido.next(newState);
    this.saveDraft(newState);
  }
  setObservaciones(observaciones?: string) {
    const newState = { ...this.value, observaciones };
    this.pedido.next(newState);
    this.saveDraft(newState);
  }
  changeDetallesCliente(key: keyof FormCliente, value: any) {
    const detalles_cliente = { ...this.value.detalles_cliente, [key]: value };
    const newState = { ...this.value, detalles_cliente };
    this.pedido.next(newState);
    this.saveDraft(newState);
  }
  resetForm() {
    const empty = {} as PedidoForm;
    this.pedido.next(empty);
    this.clearDraft();
    this._cleanse.next();
    this._montosAdded.next({ iva: 0, descuento: 0, sub_total: 0, bruto: 0 });
    this.detallesProductosEditados.next([]);
    this.productosEditados.next([]);
    this.pedidoForEdit.next({} as Pedido);
  }

  // ── Montos ───────────────────────────────────────────────
  addMonto(detalles: FormProductos) {
    let { iva, sub_total, bruto, descuento } = this._montosAdded.value;
    iva += detalles.iva_value;
    sub_total += detalles.subtotal;
    bruto += detalles.bruto;
    descuento += detalles.dto1_value + detalles.dto2_value;
    this._montosAdded.next({ iva, sub_total, bruto, descuento });
  }
  removeMonto(detalles: FormProductos) {
    let { iva, sub_total, bruto, descuento } = this._montosAdded.value;
    iva -= detalles.iva_value;
    sub_total -= detalles.subtotal;
    bruto -= detalles.bruto;
    descuento -= detalles.dto1_value - detalles.dto2_value;
    this._montosAdded.next({ iva, sub_total, bruto, descuento });
  }

  // ── Edit pedido ──────────────────────────────────────────
  async setPedidoForEdit(pedido: Pedido): Promise<true | any> {
    try {
      this.resetForm();
      this.pedidoForEdit.next(pedido);
      this.setCliente(pedido.cliente);
      this.setObservaciones(pedido.observaciones);

      // Build productos_cargados and detalles_prod_cargados arrays directly (same pattern as draft restoration)
      const productos_cargados: Producto[] = [];
      const detalles_prod_cargados: FormProductos[] = [];

      pedido.items.forEach((item) => {
        const precioModel = item.precios.find(
          (precio) =>
            precio?.lista?.id === item?.lista_precio_id ||
            precio?.lista_id === item?.lista_precio_id
        ) || item.precios[0];
        const cantidad = Number(
          parseFloat(item.cantidad.toString()).toFixed(2)
        );

        productos_cargados.push(item.producto);
        detalles_prod_cargados.push({
          id: item.id,
          unidad: item.unidad.es_bulto
            ? item.unidad.titulo +
            ' (x' +
            item.unidad?.multiplicador +
            ' ' +
            (item.producto.unidad.titulo_plural || item.producto.unidad.titulo) +
            ')'
            : item.unidad.fraccionable
              ? item.unidad.titulo
              : item.unidad.titulo,
          unidad_id: item.unidad.id_afip,
          unidadModel: item.unidad,
          precioModel,
          lista: item.lista_precio,
          lista_id: item.lista_precio_id?.toString(),
          cantidad,
          precio_lista:
            'L' + item.lista_precio_id +
            ' - $' +
            parseFloat((precioModel?.precio || 0).toString()).toFixed(2) +
            ' cada unidad',
          precio_unidad: item.precio_unitario,
          iva: item.iva.porcentaje.toString(),
          dto1: Number(
            parseFloat(item.porcentaje_descuento_1.toString()).toFixed(2)
          ),
          dto2: Number(
            parseFloat(item.porcentaje_descuento_2.toString()).toFixed(2)
          ),
          dto1_value: Number(
            ((item.descuento_1_unitario || 0) * item.cantidad).toFixed(2)
          ),
          dto2_value: Number(
            ((item.descuento_2_unitario || 0) * item.cantidad).toFixed(2)
          ),
          almacen: 'string',
          bruto: item.subtotal_bruto,
          iva_value: item.subtotal_iva,
          subtotal: item.subtotal_neto,
          impuesto: 0,
          priceBy: item.unidad_medida,
        });
      });

      // Set the state directly with the arrays (same pattern as draft restoration)
      const newState = {
        ...this.value,
        id: pedido.id,
        cliente: pedido.cliente,
        productos_cargados,
        detalles_prod_cargados,
        bruto: pedido.importe_gravado,
        ivaValor: pedido.importe_iva,
        descuentos: pedido.importe_descuento,
        total: pedido.importe_total,
        observaciones: pedido.observaciones,
      };
      this.pedido.next(newState);
      // NO actualizar los BehaviorSubjects aquí - solo actualizar el estado del pedido
      this.saveDraft(newState);

      return true;
    } catch (error) {
      console.error('[setPedidoForEdit] Error:', error);
      return error;
    }
  }

  // ── Product CRUD ─────────────────────────────────────────
  pushProducto(
    producto: Producto,
    detalles: FormProductos,
    startingEdit?: boolean
  ) {
    // Local edit only - all products go to main arrays
    // When saving, the entire pedido (including all items) will be sent to backend
    const detalles_prod_cargados = [
      ...(this.value.detalles_prod_cargados || []),
      detalles,
    ];
    const productos_cargados = [
      ...(this.value.productos_cargados || []),
      producto,
    ];
    // NO actualizar los BehaviorSubjects aquí - se actualizan solo en setValueProductos
    this.setValueProductos(
      detalles,
      detalles_prod_cargados,
      productos_cargados
    );
  }

  edditProducto(
    detalles: FormProductos,
    index: number,
    editCargado = false
  ): Observable<{
    index: number;
    previus: FormProductos;
    detalles: FormProductos;
    itemForPost: PostItem;
  }> | void {
    // Local edit only - all edits go to main arrays
    // The changes will be sent to backend when the save button is pressed
    this.persistEditProduct(index, detalles, this.value.detalles_prod_cargados[index], null);
  }

  persistEditProduct(
    index: number,
    detalles: FormProductos,
    previus: FormProductos,
    itemForPost: PostItem
  ) {
    this.value.detalles_prod_cargados[index] = detalles;
    const detalles_prod_cargados = this.value.detalles_prod_cargados;
    // NO actualizar los BehaviorSubjects aquí - solo actualizar el estado del pedido
    // Solo asignar titulo si itemForPost no es null
    if (itemForPost && itemForPost.producto) {
      itemForPost.titulo = itemForPost.producto.titulo;
    }
    const newState = {
      ...this.value,
      detalles_prod_cargados,
      bruto:
        (this.value?.bruto || 0) -
        (previus?.bruto || 0) +
        (detalles?.bruto || 0),
      ivaValor:
        this.value?.ivaValor -
        (previus?.iva_value || 0) +
        (detalles?.iva_value || 0),
      descuentos:
        this.value?.descuentos -
        (previus?.dto1_value || 0) -
        (previus?.dto2_value || 0) +
        (detalles?.dto1_value || 0) +
        (detalles?.dto2_value || 0),
      total:
        this.value?.total -
        (previus?.subtotal ? previus?.subtotal : 0) +
        (detalles?.subtotal ? detalles?.subtotal : 0),
    };
    this.pedido.next(newState);
    this.saveDraft(newState);
  }

  setValueProductos(
    detalles: FormProductos,
    detalles_prod_cargados: FormProductos[],
    productos_cargados: Producto[]
  ) {
    const newState = {
      ...this.value,
      detalles_prod_cargados,
      productos_cargados,
      bruto:
        parseFloat(this.value?.bruto?.toString() || '0') +
        parseFloat(detalles.bruto?.toString() || '0'),
      ivaValor:
        parseFloat(this.value?.ivaValor?.toString() || '0') +
        parseFloat(detalles.iva_value?.toString() || '0'),
      descuentos:
        parseFloat(this.value?.descuentos?.toString() || '0') +
        parseFloat(detalles.dto1_value?.toString() || '0') +
        parseFloat(detalles.dto2_value?.toString() || '0'),
      total:
        parseFloat(this.value?.total?.toString() || '0') +
        parseFloat(detalles.subtotal?.toString() || '0'),
    };
    this.pedido.next(newState);
    this.saveDraft(newState);
  }

  removeProduct(
    index: number,
    editCargado = false
  ): Observable<{ index: number; values: FormProductos }> | void {
    // Local edit only - all deletions go to main arrays
    // The changes will be sent to backend when the save button is pressed
    const values = this.value.detalles_prod_cargados[index];
    this.persistRemoveProduct(index, values);
  }

  persistRemoveProduct(index: number, values: FormProductos) {
    this.value.detalles_prod_cargados.splice(index, 1);
    const detalles_prod_cargados = this.value.detalles_prod_cargados;
    this.value.productos_cargados.splice(index, 1);
    const productos_cargados = this.value.productos_cargados;
    // NO actualizar los BehaviorSubjects aquí - solo actualizar el estado del pedido
    const newState = {
      ...this.value,
      detalles_prod_cargados,
      productos_cargados,
      bruto: this.value?.bruto - values.bruto,
      ivaValor: this.value?.ivaValor - values.iva_value,
      descuentos:
        this.value?.descuentos - values.dto1_value - values.dto2_value,
      total: this.value?.total - values.subtotal,
    };
    this.pedido.next(newState);
    this.saveDraft(newState);
  }

  // ── API delegates ────────────────────────────────────────
  pushItemsToDb(): Observable<{ [key: string]: any }> {
    const items = this.detallesProductosEditados.value.map((detalles, index) => {
      const item = this.totals.getItemForPost(
        detalles,
        this.productosEditados.value[index],
        this.detallesProductosEditados.value[index]
      );
      const totales = this.totals.getTotalesFromDetalles(detalles);
      item.titulo = item.producto.titulo;
      return { item, totales };
    });
    return this.api.pushItemsToDb(this.pedidoForEdit.value.id, items);
  }

  pushToDb(item: PostPedido): Observable<unknown> {
    return this.api.pushToDb(item);
  }

  actualizarMayorista(pedidoId: number, item: PostPedido): Observable<unknown> {
    return this.api.actualizarMayorista(pedidoId, item);
  }

  getForPostData(user: User, onlySave?: boolean): PostPedido {
    return this.totals.buildPostPedido(
      this.value,
      this.value.detalles_prod_cargados,
      this.value.productos_cargados,
      user,
      this.pedidoForEdit.value.id,
      onlySave
    );
  }

  finalizar(pedido: Pedido | PostPedido): Observable<any> {
    return this.api.finalizar(pedido);
  }

  actualizar(pedido: PostPedido): Observable<any> {
    return this.api.actualizar(pedido);
  }

  actualizarDatos({ observaciones }: { observaciones: string }): Observable<unknown> {
    return this.api.actualizarDatos(this.valueEdit.id, observaciones);
  }
}
